import React from 'react';
import { Helmet } from 'react-helmet-async';
import { buildLocalBusinessJsonLd, buildWebSiteJsonLd } from './jsonLd';
import { siteUrl } from './seoMeta';

/**
 * The single place any page declares its metadata.
 *
 * Before this existed, all 18 routes hand-rolled their own <Helmet> block,
 * which is how the site ended up with og:/twitter: tags on exactly two pages
 * and canonicals missing from several others.
 *
 * scripts/prerender.js waits for either a canonical link or a noindex robots
 * meta before snapshotting a page — that is its signal that Helmet has flushed
 * with real (post-fetch) data. So every page must render this component with
 * either `canonical` or `noindex` set, or it will never be prerendered.
 */

/** 1200x630 site-wide sharing image. Absolute URL — scrapers don't resolve relative paths. */
export const defaultOgImage = `${siteUrl}/images/og-default.jpg`;

/** og:image must be absolute; most callers have a site-relative path to hand. */
const toAbsolute = (url: string): string =>
  url.startsWith('http') ? url : `${siteUrl}${url.startsWith('/') ? '' : '/'}${url}`;

/**
 * Emitted on every page, ahead of the page's own schemas.
 *
 * These describe the company and the site rather than the document, so Google
 * expects them everywhere — and the page-level Service/BlogPosting/Offer nodes
 * reference the LocalBusiness by @id, which only resolves if that node is
 * present in the same document.
 *
 * They live here rather than in a second <Helmet> inside Layout because nested
 * Helmet instances do not reliably merge their <script> children: the deeper
 * instance wins, so a page rendering its own JSON-LD silently dropped the
 * site-level pair.
 */
const siteSchemas = [buildLocalBusinessJsonLd(), buildWebSiteJsonLd()];

export interface SeoProps {
  title: string;
  description: string;
  /** Absolute URL. Omit only when `noindex` is set. */
  canonical?: string;
  /** Absolute URL to a sharing image. Defaults to the sitewide one. */
  image?: string;
  type?: 'website' | 'article' | 'product';
  noindex?: boolean;
  /** Set to false on pages that are noindex but whose links should not be followed. */
  follow?: boolean;
  /**
   * Set on loading branches whose metadata is not final yet (shop pages waiting
   * on the API). It emits a marker that scripts/prerender.js blocks on, so the
   * prerenderer waits for the real page instead of freezing "Chargement…" into
   * the static HTML. Costs nothing at runtime.
   */
  transient?: boolean;
  /** One <script type="application/ld+json"> is emitted per entry. */
  jsonLd?: Array<Record<string, unknown>>;
  /** Extra tags (article:published_time, product:price:amount, …). */
  children?: React.ReactNode;
}

const Seo: React.FC<SeoProps> = ({
  title,
  description,
  canonical,
  image = defaultOgImage,
  type = 'website',
  noindex = false,
  follow = true,
  transient = false,
  jsonLd,
  children,
}) => {
  const ogImage = toAbsolute(image);

  return (
  <Helmet>
    {/* <html lang>/<dir> are owned by useHtmlLang(), which tracks i18next. */}
    <title>{title}</title>
    <meta name="description" content={description} />

    {transient ? <meta name="prerender-state" content="pending" /> : null}

    {canonical ? <link rel="canonical" href={canonical} /> : null}
    {noindex ? (
      <meta name="robots" content={follow ? 'noindex,follow' : 'noindex,nofollow'} />
    ) : (
      <meta name="robots" content="index,follow,max-image-preview:large" />
    )}

    <meta property="og:site_name" content="A2S SARL" />
    <meta property="og:locale" content="fr_MA" />
    <meta property="og:type" content={type} />
    <meta property="og:title" content={title} />
    <meta property="og:description" content={description} />
    <meta property="og:image" content={ogImage} />
    {canonical ? <meta property="og:url" content={canonical} /> : null}

    <meta name="twitter:card" content="summary_large_image" />
    <meta name="twitter:title" content={title} />
    <meta name="twitter:description" content={description} />
    <meta name="twitter:image" content={ogImage} />

    {[...siteSchemas, ...(jsonLd ?? [])].map((schema, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <script key={index} type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    ))}

    {children}
  </Helmet>
  );
};

export default Seo;
